/**
 * useAgentInbox.poll — circuit-breaker for the chat-inbox live poller.
 *
 * The chat surface polls `v_chat_inbox` (SL slug `/v1/views/chat-inbox`)
 * on a 3s live cadence across `useAgentInbox` + `useAgentInboxPaginated`,
 * fanned out over every mounted AgentInbox, the AppLayout unread badge, and
 * the push-notification watcher. When the view or SL is degraded (429
 * rate-limit storm, 5xx, llm-adapter down) that fixed 3s cadence turns into
 * a self-amplifying hammer: it makes the outage worse and burns the
 * operator's rate budget (the `v_chat_inbox` ×36 + 429 storm).
 *
 * This breaker heals to the 3s live cadence on every success but backs off
 * exponentially (capped) while the query is failing, and jitters every
 * interval so the N concurrent pollers stop bursting in lockstep. Pass it
 * as the `refetchInterval` function — react-query re-evaluates it after each
 * fetch settles, reading the live `fetchFailureCount`. A single success
 * resets `fetchFailureCount` to 0, so the breaker re-closes automatically
 * once the view recovers — no remount or manual refetch needed.
 */

const BASE_INTERVAL_MS = 3000
const MAX_BACKOFF_MS = 60000
const JITTER_RATIO = 0.2

type QueryPollStatus = 'pending' | 'error' | 'success'

/**
 * ±10% jitter so the concurrent inbox pollers de-align and stop bursting in
 * lockstep. `rand` is injectable for deterministic tests.
 */
export function jitterInterval(ms: number, rand: () => number = Math.random): number {
  const delta = ms * JITTER_RATIO * (rand() - 0.5)
  return Math.round(ms + delta)
}

/**
 * Stable jitter seed in [0,1) derived from a query's stable key (its
 * `queryHash`). Pass `() => seedFromKey(query.queryHash)` as the jitter source
 * so the jittered interval is CONSTANT across unrelated re-renders of the hook
 * owner: TanStack Query recomputes `refetchInterval` on every options update,
 * and sampling `Math.random` there would return a new number each render and
 * thrash the poll timer (clear/restart), stalling the poller until renders
 * quiesce (codex P2 on #1845). Same query → same seed (stable); distinct inbox
 * queries (different agent/session/lane) → distinct seeds (still de-aligned).
 * FNV-1a over the key, mapped to [0,1).
 */
export function seedFromKey(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return ((h >>> 0) % 100000) / 100000
}

/** Minimal slice of react-query's QueryState the breaker reads. */
export interface InboxPollState {
  status: QueryPollStatus
  /** Increments once per failed poll; NOT reset mid-fetch (unlike fetchFailureCount). */
  errorUpdateCount: number
  /** ms epoch of the last SUCCESSFUL fetch; 0 before the first-ever success. */
  dataUpdatedAt: number
}

/**
 * Circuit-breaker refetch interval for the chat-inbox poller.
 *
 *   healthy / not-yet-errored → ~3s (jittered live cadence)
 *   failing                   → escalates toward a 60s trickle, then caps
 *
 * Escalation is driven by TIME SINCE LAST SUCCESS (`dataUpdatedAt`), NOT
 * `fetchFailureCount`: react-query resets fetchFailureCount to 0 at the start
 * of every fetch (and the default retry:1 means it only ever reaches ~2 per
 * poll), so a count-based backoff would plateau at ~12s during a sustained
 * outage instead of reaching 60s (codex P2 #1845). `dataUpdatedAt` only
 * advances on a successful fetch, so `now - dataUpdatedAt` grows across
 * consecutive failed polls (interval≈elapsed → next elapsed≈2×, i.e.
 * exponential-style) and snaps back to ~0 the instant a poll succeeds — a
 * self-resetting escalation. Before the first-ever success
 * (`dataUpdatedAt === 0`) we fall back to `errorUpdateCount`, which — unlike
 * fetchFailureCount — increments once per failed poll and is not reset mid-fetch.
 *
 * Returns a number (never `false`): a degraded inbox keeps a 60s trickle so it
 * recovers on its own once SL/the view come back instead of going stale.
 */
export function inboxRefetchInterval(
  state: InboxPollState,
  now: number,
  rand: () => number = Math.random,
): number {
  if (state.status !== 'error') return jitterInterval(BASE_INTERVAL_MS, rand)
  const backoff =
    state.dataUpdatedAt > 0
      ? Math.min(Math.max(now - state.dataUpdatedAt, BASE_INTERVAL_MS), MAX_BACKOFF_MS)
      : Math.min(BASE_INTERVAL_MS * 2 ** state.errorUpdateCount, MAX_BACKOFF_MS)
  return jitterInterval(backoff, rand)
}

export const INBOX_POLL = { BASE_INTERVAL_MS, MAX_BACKOFF_MS, JITTER_RATIO } as const
